/**
 * Reader auth helpers for the docs SPA.
 *
 * All sign-in flows go through the **same-origin** `/api/auth/*` Pages
 * Functions (see `functions/api/auth/`) rather than hitting the Siena API
 * directly. That gives us three things the old browser-to-API layout
 * couldn't:
 *
 *   1. The upstream API endpoints (`/v1/docs/public/readers/*`) are gated
 *      by `apiKeyAuthorization()` and only accept requests from the Pages
 *      Function (which carries the shared `SIENA_API_KEY`). Direct hits
 *      from anywhere on the internet get 403 instantly.
 *
 *   2. The subdomain is stamped by the edge (the `docs-edge` Worker sets
 *      `X-Forwarded-Subdomain`) instead of being supplied by the client.
 *      A malicious caller can no longer request a magic link for a
 *      tenant they don't own just by editing the request body.
 *
 *   3. Per-(IP, tenant) rate limits and per-email cooldowns live in the
 *      Pages Function so abuse is blunted before it reaches the API.
 *      See `functions/api/auth/_shared.ts`.
 *
 * After successful verification we still store the reader JWT as a
 * first-party cookie (`docs_reader_token`). The Pages `_middleware.ts`
 * verifies that cookie on every `/sites/...` fetch, so content access
 * is unchanged.
 */

const COOKIE_NAME = "docs_reader_token";
/**
 * Matches `DEFAULT_TTL_SECONDS` on the API's reader JWT (90 days). Keep the
 * two in sync — a shorter cookie silently ends the session while the token
 * is still valid, forcing a needless magic-link round-trip.
 */
const COOKIE_MAX_AGE = 60 * 60 * 24 * 90;

export interface ReaderPolicy {
  space_uuid: string;
  subdomain: string;
  name: string;
  emoji: string | null;
  branding: {
    logo_url?: string;
    logo_dark_url?: string;
    favicon_url?: string;
    homepage_heading?: string;
    homepage_description?: string;
  };
  visibility: "public" | "internal" | "logged_in";
  reader_mode: "open" | "invite_only";
  reader_requires_approval: boolean;
  reader_allowed_domains: string[];
}

export interface ReaderPrincipal {
  uuid: string;
  email: string;
  name: string | null;
}

export type VerifyResponse =
  | {
      status: "ok";
      token: string;
      reader: ReaderPrincipal;
      redirect_to: string | null;
    }
  | {
      status: "pending_approval";
      email: string;
      name: string | null;
    }
  | {
      status: "revoked";
      email: string;
    };

export type RequestMagicLinkResponse =
  | { status: "sent" }
  | { status: "pending_approval" }
  | { status: "domain_not_allowed"; allowed_domains: string[] }
  | { status: "revoked" }
  | { status: "silent" };

async function callApi<T>(path: string, init?: RequestInit): Promise<T> {
  // Same-origin — no CORS preflights, no custom headers needed. The
  // subdomain is injected by the Pages Function from the edge header,
  // so no tenant identifier crosses the browser boundary.
  const res = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {})
    }
  });
  const body = (await res.json().catch(() => null)) as {
    data?: T;
    meta?: { message?: string };
  } | null;
  if (!res.ok) {
    const msg = body?.meta?.message ?? `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return (body?.data as T) ?? (null as unknown as T);
}

export async function fetchReaderPolicy(): Promise<ReaderPolicy> {
  return callApi<ReaderPolicy>("/api/auth/policy");
}

export async function requestMagicLink(input: {
  email: string;
  name: string;
  redirect_to?: string | null;
}): Promise<RequestMagicLinkResponse> {
  return callApi<RequestMagicLinkResponse>("/api/auth/request-link", {
    method: "POST",
    body: JSON.stringify({
      email: input.email,
      name: input.name,
      redirect_to: input.redirect_to ?? null
    })
  });
}

export async function verifyMagicLink(token: string): Promise<VerifyResponse> {
  return callApi<VerifyResponse>("/api/auth/verify", {
    method: "POST",
    body: JSON.stringify({ token })
  });
}

// ─── Session cookie helpers ────────────────────────────────────────────────

/**
 * Sets a first-party cookie that the Cloudflare middleware reads when the
 * SPA fetches `/sites/...`. We stay on the same origin so SameSite=Lax is
 * enough and we do not need an HttpOnly cookie (the reader token is already
 * a short-lived, space-scoped JWT; XSS would already have access to the
 * reader's content via the SPA runtime).
 *
 * IMPORTANT: we intentionally do NOT set a `Domain=` attribute. Per RFC
 * 6265 that means the browser host-scopes the cookie to the exact
 * hostname that set it (e.g. `acme-dev.docs.siena.cx`). Do not widen this
 * to `.docs.siena.cx` — that would leak reader tokens across tenants and
 * across envs (dev ↔ prod). The JWT audience (`DOCS_READER_JWT_AUDIENCE`)
 * is already env-specific, but the cookie boundary is our first line of
 * defence against a curious user copying their cookie to another tab.
 */
export function setReaderCookie(token: string): void {
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
    token
  )}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

export function clearReaderCookie(): void {
  // Mirror the attributes used when the cookie was written. Some browsers
  // (notably older Safari) require the `Secure` bit on deletion when the
  // original cookie had it, and mismatched attributes can leave a zombie
  // entry that the Pages middleware still sees on the next request.
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  // Belt-and-braces: also clear with Expires=epoch for very old UAs.
  document.cookie = `${COOKIE_NAME}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${secure}`;
}

export function hasReaderCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .map((c) => c.trim())
    .some((c) => c.startsWith(`${COOKIE_NAME}=`));
}

function readCookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  return decodeURIComponent(match.slice(name.length + 1));
}

function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad =
    padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  try {
    return atob(padded + pad);
  } catch {
    return "";
  }
}

/**
 * Decodes the reader JWT payload from the first-party cookie WITHOUT
 * verifying the signature. This is safe for UI display (name/email/avatar
 * initials) because the Cloudflare Pages middleware still verifies the token
 * on every protected fetch — a tampered cookie cannot unlock content.
 *
 * Returns null if the cookie is missing, malformed, or expired.
 */
export function getReaderFromCookie(): ReaderPrincipal | null {
  const raw = readCookieValue(COOKIE_NAME);
  if (!raw) return null;
  const parts = raw.split(".");
  if (parts.length < 2) return null;
  const payloadJson = base64UrlDecode(parts[1]);
  if (!payloadJson) return null;
  try {
    const payload = JSON.parse(payloadJson) as {
      reader_uuid?: string;
      email?: string;
      name?: string | null;
      exp?: number;
    };
    if (payload.exp && payload.exp * 1000 < Date.now()) return null;
    if (!payload.reader_uuid || !payload.email) return null;
    return {
      uuid: payload.reader_uuid,
      email: payload.email,
      name: payload.name ?? null
    };
  } catch {
    return null;
  }
}

/**
 * Signs the reader out. Clears the cookie locally and fires a best-effort
 * call to the proxy sign-out endpoint (currently a no-op upstream, kept
 * for symmetry and future server-side revocation).
 */
export async function signOutReader(): Promise<void> {
  clearReaderCookie();
  try {
    await fetch("/api/auth/sign-out", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: "{}",
      keepalive: true
    });
  } catch {
    // Ignore — sign-out is client-driven.
  }
}
